import { StyledInput } from '@features/operations/components/StreamTableComps/DropDownWithInput'
import type { ControllableApplication } from 'api-validations/stitch'
import { Fragment } from 'react'
import {
  type Control,
  type UseFormSetFocus,
  type UseFormSetValue,
  useFieldArray,
  useWatch
} from 'react-hook-form'
import {
  ButtonKind,
  Icon,
  MeatballMenu,
  MeatballMenuItem,
  PanoramaButton
} from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { useLocalization } from 'shared-ui/localization'
import {
  createZone,
  deleteZone,
  setActiveZone,
  useActiveZone,
  useCurrentZones,
  useMapContext
} from 'shared-ui/map'
import { isNonNil } from 'utils'
import { Averageable } from 'utils/metric/averageable'

export type UnitType = {
  name: string
  type: ControllableApplication
  unit: string
}
export type Unit = {
  id: string
  type: UnitType
  name: string
  attributes: {
    name: string
    zones: { [id: string]: number }
  }[]
}

export type Zone = {
  id: string
  name: string
  color: string
}

export type FormValues = {
  unitToAdd: UnitType
  units: Unit[]
}

export const Attributes = ({
  index,
  control
}: {
  control: Control<FormValues, unknown, FormValues>
  index: number
}) => {
  const { t } = useLocalization()
  const { append, remove, fields } = useFieldArray({
    control,
    name: `units.${index}.attributes`
  })

  return (
    <div className="flex flex-col gap-4">
      <label className="pan-label block w-full border-b border-gray-300 pb-3">
        <div className="mb-2">{t(($) => $.planning.boundaryCreation.prescriptionName)}</div>
        <StyledInput
          {...control.register(`units.${index}.name`, {
            validate: (value) =>
              value.length > 0 || t(($) => $.planning.boundaryCreation.prescriptionsMustBeNamed)
          })}
          className="w-full"
        />
        {control._formState?.errors?.units?.[index]?.name && (
          <div className="text-sm text-red-600">
            {control._formState?.errors.units[index].name.message}
          </div>
        )}
      </label>

      <PanoramaButton
        type="button"
        kind={ButtonKind.Secondary}
        className="w-full"
        icon={SpriteMap.AddCircleIcon}
        onClick={() => append({ name: '', zones: {} })}
      >
        {t(($) => $.planning.boundaryCreation.addAttribute)}
      </PanoramaButton>
      {fields.map((field, i) => (
        <div key={field.id} className="flex w-full items-start gap-4">
          <label className="pan-label block w-full">
            <div className="mb-2">
              {t(($) => $.planning.boundaryCreation.attributeNumber, {
                number: i + 1
              })}
            </div>
            <StyledInput
              {...control.register(`units.${index}.attributes.${i}.name`, {
                validate: (value) =>
                  value.length > 0 || t(($) => $.planning.boundaryCreation.attributesMustBeNamed)
              })}
            />
            {control._formState?.errors?.units?.[index]?.attributes?.[i]?.name && (
              <div className="text-sm text-red-600">
                {control._formState?.errors.units[index].attributes[i].name.message}
              </div>
            )}
          </label>
          <button
            type="button"
            disabled={fields.length === 1}
            className="pb-3 text-black disabled:opacity-30"
            onClick={() => remove(i)}
          >
            <Icon icon={SpriteMap.TrashIcon} className="mt-10 size-5 text-red-500" />
          </button>
        </div>
      ))}
    </div>
  )
}

export const RateGroups = ({
  viewState,
  control,
  setFocus,
  setValue,
  areas
}: {
  viewState?: 'Edit' | 'View'
  setFocus: UseFormSetFocus<FormValues>
  control: Control<FormValues, unknown, FormValues>
  setValue: UseFormSetValue<FormValues>
  areas: { area: number; zones: Record<string, number> }
}) => {
  const { t, formatMetric } = useLocalization()
  const { map } = useMapContext()

  const units = useWatch({ name: 'units', control }) ?? []

  const rateZone = useActiveZone()
  const zones = useCurrentZones()

  const columnCount = units.reduce((count, { attributes }) => count + attributes.length, 1)
  return (
    <div className="relative -mx-6 flex flex-col gap-2 py-4">
      <div className="flex items-center justify-between px-6">
        {' '}
        <h5 className="pan-title4">{t(($) => $.titles.rateZones)}</h5>
        {viewState !== 'View' && (
          <PanoramaButton
            type="button"
            icon={SpriteMap.AddCircleIcon}
            kind={ButtonKind.Secondary}
            onClick={() => {
              const newZone = createZone({
                name: `${t(($) => $.labels.zone, { count: 1 })} ${(zones?.zones.size ?? 0) + 1}`
              })
              units.forEach((unit, unitIdx) => {
                unit.attributes.forEach((_, attrIdx) => {
                  setValue(`units.${unitIdx}.attributes.${attrIdx}.zones.${newZone.id}`, 0)
                })
              })
            }}
          >
            {t(($) => $.buttons.add)}
          </PanoramaButton>
        )}
      </div>
      <div
        className="grid gap-x-4 overflow-x-scroll px-6"
        style={{
          gridTemplateColumns: `20px minmax(min-content, 300px) minmax(75px, 100px) repeat(${columnCount}, minmax(min-content, 300px)) 2px`
        }}
      >
        <h6 className="pan-title5 col-span-2 row-span-2 self-end pl-8 font-normal">
          {t(($) => $.labels.zone, { count: 1 })}
        </h6>
        <h6 className="pan-title5 row-span-2 self-end font-normal">{t(($) => $.acres)}</h6>
        {units.map(({ id, name, attributes, type: { unit } }) => (
          <div
            key={id}
            className="row-span-2 grid grid-cols-subgrid"
            style={{
              gridColumn: `span ${attributes.length} / span ${attributes.length}`
            }}
          >
            <div key={id} className="col-span-full flex gap-2 text-nowrap">
              <h6 className="pan-title5">{name}</h6>
              <div className="text-gray-500">{unit}</div>
            </div>
            {attributes.map(({ name }, i) => (
              <h6 className="pan-title5 font-normal" key={`${id}${i}`}>
                {name}
              </h6>
            ))}
          </div>
        ))}
        {[...(zones?.zones.values() ?? [])].map((zone) => {
          const zoneArea = areas.zones[zone.id] ?? 0
          return (
            <Fragment key={zone.id}>
              <label
                htmlFor={zone.id}
                className="col-start-1 col-end-auto grid grid-cols-subgrid items-center gap-4 rounded-lg py-2"
                style={{
                  ...(rateZone === zone ? { background: `${zone.color}55` } : {}),
                  gridColumn: '1 / -1'
                }}
              >
                <input
                  id={zone.id}
                  onChange={({ target }) => {
                    if (target.checked) {
                      setFocus(`units.0.attributes.0.zones.${zone.id}`, {
                        shouldSelect: true
                      })
                      map.drawTool.setSelected(zone.id)
                    }
                  }}
                  checked={rateZone === zone}
                  name="rateZone"
                  type="radio"
                  className="col-start-1 ml-2 h-4 self-center"
                />
                <h6
                  className="pan-title5 w-fit text-nowrap rounded-md p-2 px-4"
                  style={{
                    background: zone.color
                  }}
                >
                  {zone.name}
                </h6>
                <h6 className="pan-title5">{formatMetric({ value: zoneArea, unit: 'ac' })}</h6>
                {units?.map((unit, index) =>
                  unit.attributes.map((_, i) => (
                    <StyledInput
                      className="min-w-20 field-sizing-content"
                      disabled={viewState === 'View'}
                      key={`${unit.id}${i}`}
                      onWheel={(e) => e.currentTarget.blur()}
                      type="number"
                      step="any"
                      onFocus={(e) => {
                        map.drawTool.setSelected(zone.id)
                        e.target.select()
                      }}
                      {...control.register(`units.${index}.attributes.${i}.zones.${zone.id}`, {
                        value: 0,
                        required: true,
                        valueAsNumber: true
                      })}
                    />
                  ))
                )}
                {viewState !== 'View' && (
                  <MeatballMenu menuItemsWidth="w-fit">
                    <MeatballMenuItem
                      color="danger"
                      disabled={zoneArea > 0}
                      icon={<Icon icon={SpriteMap.TrashIcon} className="size-5" />}
                      title={t(($) => $.remove)}
                      onClick={() => deleteZone(zone.id)}
                      description={
                        zoneArea > 0 ? t(($) => $.planning.removeZoneDescription) : undefined
                      }
                    />
                  </MeatballMenu>
                )}
              </label>
            </Fragment>
          )
        })}
        <ApplicationTotals control={control} areas={areas} />
      </div>
    </div>
  )
}

const ApplicationTotals = ({
  control,
  areas
}: {
  control: Control<FormValues, unknown, FormValues>
  areas: { area: number; zones: Record<string, number> }
}) => {
  const { t } = useLocalization()
  const units = useWatch({ name: 'units', control }) ?? []
  const activeZone = useActiveZone()
  const allAttributes = units.flatMap(({ attributes }) => attributes)

  const getZoneTotal = (attZones: { [id: string]: number }, zoneId: string): number =>
    (attZones[zoneId] ?? 0) * (areas.zones[zoneId] ?? 0)

  const getZoneAverage = (attZones: { [id: string]: number }, zoneId: string): number =>
    attZones[zoneId] ?? 0

  const getAllZonesTotals = (attZones: { [id: string]: number }): number =>
    Object.entries(attZones).reduce(
      (acc, [id, value]) => acc + (value ?? 0) * (areas.zones[id] ?? 0),
      0
    )

  const getAllZonesAverage = (attZones: { [id: string]: number }): number =>
    Object.entries(attZones).reduce(
      (acc, [id, value]) =>
        Averageable.combine(
          Averageable.fromNullable({ weight: areas.zones[id] ?? 0, value: value ?? 0 }),
          acc
        ),
      Averageable.fromNullable({ weight: 0, value: 0 })
    ).value

  return (
    <div
      className="col-start-1 col-end-auto mt-2 grid grid-cols-subgrid items-center divide-y divide-gray-200 rounded-xl bg-gray-50 p-4"
      style={{
        gridColumn: '1 / -1'
      }}
    >
      {activeZone && (
        <div
          className="col-start-1 col-end-auto grid grid-cols-subgrid items-center gap-2 pb-2"
          style={{
            gridColumn: '1 / -1'
          }}
        >
          <TotalsRow
            label={`${activeZone.name} ${t(($) => $.planning.boundaryCreation.totals)}`}
            values={allAttributes.map((att) => getZoneTotal(att.zones, activeZone.id))}
          />
          <TotalsRow
            label={`${activeZone.name} ${t(($) => $.planning.boundaryCreation.averages)}`}
            values={allAttributes.map((att) => getZoneAverage(att.zones, activeZone?.id))}
          />
        </div>
      )}
      <div
        className="col-start-1 col-end-auto grid grid-cols-subgrid items-center gap-2 rounded-lg pt-2"
        style={{
          gridColumn: '1 / -1'
        }}
      >
        <TotalsRow
          label={t(($) => $.planning.boundaryCreation.allZonesTotals)}
          values={allAttributes.map((att) => getAllZonesTotals(att.zones))}
        />
        <TotalsRow
          label={t(($) => $.planning.boundaryCreation.allZonesAverages)}
          values={allAttributes.map((att) => getAllZonesAverage(att.zones))}
        />
      </div>
    </div>
  )
}

const TotalsRow = ({ label, values }: { label: string; values: number[] }) => {
  const { formatMetric } = useLocalization()
  return (
    <>
      <div className="col-span-3 font-bold">{label}</div>
      {values.map((value, i) => (
        <div key={i} className="px-3">
          <span className="font-bold text-gray-900">{formatMetric({ value })}</span>
        </div>
      ))}
    </>
  )
}
